/**
 * Homepage behaviour. Deliberately small.
 *
 * The page is useful with no JavaScript: the try band degrades to its own
 * explanation rather than to a broken shell. Nothing here imports the viewer —
 * that is 627 KB and it waits behind a press, in `body-pane.ts`.
 *
 * This file is only the coordinator: it owns the two pieces of state the
 * playground has — which garment, and whose artwork — and hands changes to the
 * map and the body. Phase 1 scaffolds the path; the fuller error handling and
 * the polish are phase 2.
 */

import { coverageOf } from "../../shared/coverage";
import { CANVAS, REGION_IDS } from "../../shared/format";
import { emptyFacesNote } from "../../shared/region-facts";
import { createBodyPane } from "./body-pane";
import { createRegionMap } from "./region-map";
import { sampleTemplate } from "./sample";
import { createSeamSlider } from "./seam-slider";
import { checkFile, checkSize, drawTemplate } from "./template-file";

const mapSlot = document.querySelector("[data-try-slot]") as HTMLElement | null;
const bodySlot = document.querySelector("[data-body-slot]") as HTMLElement | null;

if (mapSlot) {
  let garment: "shirt" | "pants" = "shirt";

  const body = bodySlot ? createBodyPane(garment) : null;
  const map = createRegionMap({
    garment,
    // The whole point: choosing on the template drives the body. `highlightRegion`
    // had existed on the viewer, unused, since it was written.
    onSelect: (id) => body?.show(id),
  });

  // `replaceChildren`, which the DOM lib provides and workers-types does not
  // shadow — unlike `append`. See the note in region-map.ts.
  mapSlot.replaceChildren(map.el);
  if (body && bodySlot) {
    bodySlot.replaceChildren(body.el);
    // And back the other way, so the two panels feel like one object rather
    // than a diagram beside a toy.
    body.onPick((id) => map.select(id));
  }

  // ------------------------------------------------------------- garment

  const msg = document.querySelector("[data-upload-msg]") as HTMLElement | null;

  function say(text: string, tone: "info" | "warn" = "info") {
    if (!msg) return;
    msg.textContent = text;
    msg.dataset.tone = tone;
    msg.hidden = !text;
  }

  for (const btn of document.querySelectorAll<HTMLButtonElement>("[data-garment]")) {
    btn.addEventListener("click", () => {
      const next = btn.dataset.garment === "pants" ? "pants" : "shirt";
      if (next === garment) return;
      garment = next;
      for (const b of document.querySelectorAll<HTMLButtonElement>("[data-garment]")) {
        b.setAttribute("aria-pressed", String(b.dataset.garment === next));
      }
      /*
       * Both, always. The map relabels every face — the same rectangle is a
       * sleeve on one garment and a leg on the other — and the body re-binds
       * which parts wear the texture. Updating one without the other is how a
       * label ends up describing a different garment than the one on screen.
       */
      map.setGarment(next);
      body?.setGarment(next);
      // The old selection's occluders belonged to the other garment.
      map.select(null);
      // The export is garment-specific — the seam correction pairs faces
      // against the body this garment wraps.
      if (scratch) void seam?.show(scratch, next);
    });
  }

  // -------------------------------------------------------------- upload

  const drop = document.querySelector("[data-drop]") as HTMLElement | null;
  const input = document.querySelector("[data-file]") as HTMLInputElement | null;
  const clear = document.querySelector("[data-clear]") as HTMLButtonElement | null;

  /** Rejects a slow decode that lands after a newer file — or after a clear. */
  let dropSeq = 0;

  const seamSlot = document.querySelector("[data-seam-slot]") as HTMLElement | null;
  const seam = seamSlot ? createSeamSlider() : null;
  if (seam && seamSlot) seamSlot.replaceChildren(seam.el);

  const sample = document.querySelector("[data-sample]") as HTMLButtonElement | null;

  sample?.addEventListener("click", () => {
    /*
     * Straight to `show`, skipping every file check: it is a canvas we drew at
     * exactly CANVAS size a moment ago, so validating it would be validating
     * our own arithmetic.
     */
    dropSeq++;
    const canvas = sampleTemplate();
    show(canvas, { width: canvas.width, height: canvas.height });
    say("A sample garment. Drop your own template to replace it.");
  });

  clear?.addEventListener("click", () => {
    // Newer than any decode still in flight, so a slow file cannot land after
    // the user has asked for a blank template.
    dropSeq++;
    map.setArtwork(null);
    map.setNote(null);
    body?.setArtwork(null);
    // Back to the selection state too. Leaving a face lit after its artwork is
    // gone points at nothing.
    map.select(null);
    clear.hidden = true;
    void seam?.show(null, garment);
    say("");
  });

  /**
   * Which faces the artist left blank.
   *
   * Measured from a scratch canvas at exactly the template size, so a
   * scaled-to-fit image is measured as it will actually wrap rather than as it
   * arrived. Reused between uploads: allocating a 585x559 canvas per drop is a
   * megabyte of churn for an answer thrown away a moment later.
   */
  let scratch: HTMLCanvasElement | null = null;

  function describeGaps(
    source: CanvasImageSource,
    size: { width: number; height: number },
  ): string | null {
    try {
      scratch ??= document.createElement("canvas");
      scratch.width = CANVAS.width;
      scratch.height = CANVAS.height;
      const ctx = scratch.getContext("2d", { willReadFrequently: true });
      if (!ctx) return null;
      drawTemplate(scratch, source, size);
      const cover = coverageOf(ctx.getImageData(0, 0, scratch.width, scratch.height));
      return emptyFacesNote(cover.emptyRegions, REGION_IDS.length, garment);
    } catch {
      // A note is a nicety. Failing to produce one must never cost the upload.
      return null;
    }
  }

  /**
   * Put a template on both panes.
   *
   * Shared by the file path and the sample button so they cannot diverge — the
   * sample is not a special case, it is the same artwork arriving by a
   * different door.
   */
  function show(source: CanvasImageSource, size: { width: number; height: number }) {
    map.setArtwork(source, size);
    body?.setArtwork(source, size);
    if (clear) clear.hidden = false;
    map.setNote(describeGaps(source, size));
    body?.load();

    /*
     * The seam band runs the export twice, so it is deliberately last and
     * deliberately not awaited: the panes should fill immediately and the
     * comparison can arrive a moment later.
     *
     * It needs a canvas rather than the bitmap — `prepareExport` reads pixels
     * back — and `describeGaps` has already drawn this artwork into one at
     * exactly template size, which is the same thing the export wants.
     */
    void seam?.show(scratchOf(source, size), garment);
  }

  /**
   * The artwork on a canvas at exactly template size.
   *
   * Reuses the same scratch canvas the empty-face measurement fills, because
   * both want the identical thing and a second 585×559 buffer per upload is a
   * megabyte for nothing.
   */
  function scratchOf(
    source: CanvasImageSource,
    size: { width: number; height: number },
  ): HTMLCanvasElement | null {
    if (!scratch) return null;
    drawTemplate(scratch, source, size);
    return scratch;
  }

  async function accept(file: File) {
    const pre = checkFile(file);
    if (pre.kind !== "ok") {
      say(pre.message, "warn");
      return;
    }

    const mine = ++dropSeq;
    say("Reading your template…");

    let bitmap: ImageBitmap;
    try {
      bitmap = await createImageBitmap(file);
    } catch {
      if (mine !== dropSeq) return;
      say("That file could not be opened as an image.", "warn");
      return;
    }

    /*
     * A newer file won while this one was decoding.
     *
     * Drawing it anyway would put the older artwork on the body and leave the
     * newer one's message on screen — the two would disagree, and the file the
     * person chose last would be the one they could not see.
     */
    if (mine !== dropSeq) {
      bitmap.close();
      return;
    }

    const size = { width: bitmap.width, height: bitmap.height };

    /*
     * A zero-size decode is a corrupt file that `createImageBitmap` accepted.
     * Drawing it clears the canvas to nothing, which looks like the upload
     * having wiped their work.
     */
    if (size.width === 0 || size.height === 0) {
      bitmap.close();
      say("That image has no pixels in it.", "warn");
      return;
    }
    const fit = checkSize(size.width, size.height);
    /*
     * Shown anyway when the size is wrong.
     *
     * Somebody testing the tool with whatever PNG is nearest should still see
     * it work — refusing outright teaches them nothing about the product. The
     * notice stays up while it is displayed, so the wrong size is never a
     * silent surprise later.
     */
    /*
     * Two things can be worth saying, and a WebP that is also the wrong size
     * should hear both rather than only the last one.
     *
     * WebP is accepted on purpose: it previews perfectly here, and refusing it
     * would block somebody testing the tool. But Roblox takes PNG only, so
     * loading one without a word sets up a failure at the Creator Dashboard
     * that would look like our fault.
     */
    const notes: string[] = [];
    if (fit.kind !== "ok") notes.push(`${fit.message} Showing it scaled to fit.`);
    if (/webp$/i.test(file.type) || /\.webp$/i.test(file.name)) {
      notes.push("Previews fine here, but Roblox only accepts PNG for clothing.");
    }
    say(notes.join(" "), "warn");

    /*
     * Both panes, from the one decode. The template pane shows the artwork with
     * the faces as outlines over it; the body wears it. They are drawn from the
     * same bitmap so they cannot disagree about what was loaded.
     */
    show(bitmap, size);
    /*
     * Released the moment it is drawn. An ImageBitmap holds a full decoded
     * frame — 585x559 RGBA is 1.3 MB, and a large one far more — and nothing
     * frees it for us. Dropping a dozen files without this is a dozen frames
     * held until the tab closes.
     */
    bitmap.close();
  }

  input?.addEventListener("change", () => {
    const file = input.files?.[0];
    if (file) void accept(file);
    // Cleared so choosing the same file twice fires again — otherwise a second
    // attempt after an error looks like the button is dead.
    input.value = "";
  });

  if (drop) {
    for (const type of ["dragenter", "dragover"]) {
      drop.addEventListener(type, (e) => {
        e.preventDefault();
        drop.dataset.over = "";
      });
    }
    for (const type of ["dragleave", "drop"]) {
      drop.addEventListener(type, () => {
        delete drop.dataset.over;
      });
    }
    drop.addEventListener("drop", (e) => {
      e.preventDefault();
      const file = (e as DragEvent).dataTransfer?.files?.[0];
      if (file) void accept(file);
    });
  }

  /*
   * A file dropped anywhere else must not navigate the page away.
   *
   * The browser's default for a dropped image is to open it, which loses
   * everything on screen — and the drop target is small, so missing it is easy.
   */
  for (const type of ["dragover", "drop"]) {
    document.addEventListener(type, (e) => {
      if (drop?.contains(e.target as Node)) return;
      e.preventDefault();
    });
  }
}
